import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useIsFetching } from '@tanstack/react-query';
import type { EcranDuTuto, VueDeVisite } from '../partage/api/types';
import { useEcranDuTuto } from './ecranDuTuto';
import { useAllerA, useGesteDuTuto } from './useTuto';
import { placeDeLaBulle, type Place } from './placeDeLaBulle';
import { AnneauTuto } from './AnneauTuto';
import { TexteDuTuto } from './TexteDuTuto';
import { trouverLaZone } from './zonesDuTuto';
import { JOUR_AUTOUR, useZoneALEcran, type Rectangle } from './zoneALEcran';
import './VisiteTuto.css';

/**
 * La visite façon jeu, ticket 33.
 *
 * L'écran s'assombrit, une seule zone reste éclairée, et une bulle blanche à côté explique
 * « ça, c'est là, ça sert à ça ». Le trou de lumière suit le vrai élément de la page
 * (`zonesDuTuto.ts`), à toute taille de fenêtre, et glisse d'une étape à l'autre.
 *
 * Deux sortes de bulles. Une lecture : « suivant », et rien d'autre ne se clique. Un geste
 * (« Clique « Projets » ») : la zone est entourée de l'anneau (`AnneauTuto.tsx`), elle seule reste
 * cliquable, et l'étape avance quand le clic est fait dans la vraie app : c'est le serveur
 * qui le voit, par l'écran que le client lui signale.
 *
 * Une zone qui n'existe pas à l'écran (pas de débloquant, pas d'étiquette…) fait passer
 * l'étape, une fois la page chargée : la visite ne reste jamais bloquée sur un trou, ni sur
 * un écran où elle n'a rien à montrer.
 */

/** Le temps laissé à une page pour dessiner la zone, une fois ses données là. */
const ATTENTE_ZONE_MS = 1500;

function lieuDe(ecran: EcranDuTuto | null): EcranDuTuto['lieu'] | null {
  return ecran?.lieu ?? null;
}

export function VisiteTuto({ vue, enCours }: { vue: VueDeVisite; enCours: boolean }) {
  const geste = useGesteDuTuto();
  const allerA = useAllerA();
  const ecran = useEcranDuTuto();
  const chargements = useIsFetching();
  const zone = vue.zone;
  const { rectangle: rect, trouvee } = useZoneALEcran(zone ? () => trouverLaZone(zone) : null, vue.etape);

  // Pas sur l'écran de l'étape (reprise depuis « Projets »…) : on y va, une fois par étape.
  const ecranIci = useRef(ecran);
  ecranIci.current = ecran;
  useEffect(() => {
    if (lieuDe(ecranIci.current) !== vue.lieu) allerA(vue.cible);
    // Seulement à l'arrivée d'une étape : après un clic qui change d'écran, l'étape d'avant
    // reste affichée le temps de la réponse, et ne doit pas ramener en arrière.
  }, [vue.etape]);

  // La zone manque, la page chargée : l'étape se passe. Où que l'on soit, même sur l'écran de
  // repli où l'étape nous a envoyés faute de mieux : sinon, rien à cliquer et aucune sortie.
  const passer = useRef(() => {});
  passer.current = () => geste.mutate({ geste: 'passer', vue });
  const manque = zone !== null && !trouvee && !enCours && chargements === 0 && !geste.isPending;
  useEffect(() => {
    if (!manque) return;
    const minuteur = setTimeout(() => passer.current(), ATTENTE_ZONE_MS);
    return () => clearTimeout(minuteur);
  }, [manque, vue.etape]);

  const occupe = geste.isPending;
  const faire = (quoi: 'passer' | 'quitter') => geste.mutate({ geste: quoi, vue });
  const centre = vue.sorte === 'centre';
  // Une étape avec zone attend de l'avoir trouvée : pas de bulle qui flotte sur rien.
  if (!centre && !rect) return <div className="visite-bouclier" aria-hidden="true" />;

  const trou = rect && !centre ? agrandi(rect) : null;

  return (
    <div className="visite" role="dialog" aria-modal="true" aria-label="Visite de Nextilt">
      {/* Ce qui empêche de cliquer ailleurs : tout l'écran, ou autour du trou pour un geste. */}
      {vue.sorte === 'clic' && trou && trouvee ? <Autour trou={trou} /> : <div className="visite-bouclier" />}

      {/*
        Le voile, percé d'un trou aux coins arrondis. Un masque SVG plutôt que l'ombre immense
        de la maquette : Chrome ne peint pas une ombre de 4000 px (vu à la vérification).
      */}
      <svg className="visite-voile" aria-hidden="true">
        <defs>
          <mask id="visite-trou">
            <rect width="100%" height="100%" fill="white" />
            {/* Toujours là, réduit à un point sans zone : Chrome ne repeint pas un masque
                dont on retire le trou (vu à la vérification), il le laisse ouvert. */}
            <rect
              className="visite-trou"
              style={
                trou
                  ? { x: trou.x, y: trou.y, width: trou.largeur, height: trou.hauteur }
                  : { x: window.innerWidth / 2, y: window.innerHeight / 2, width: 0, height: 0 }
              }
              fill="black"
            />
          </mask>
        </defs>
        <rect className="visite-ombre" width="100%" height="100%" mask="url(#visite-trou)" />
      </svg>

      {/* Ticket 45 : une lecture borde son trou d'un liseré clair, un geste l'entoure de l'anneau. */}
      {vue.sorte === 'lu' && trou && (
        <div
          className="visite-cadre"
          style={{ left: trou.x, top: trou.y, width: trou.largeur, height: trou.hauteur }}
          aria-hidden="true"
        />
      )}
      {vue.sorte === 'clic' && rect && trouvee && <AnneauTuto zone={rect} className="visite-anneau" />}

      {/* Le trou glisse vers la nouvelle zone ; la bulle attend qu'elle soit trouvée. */}
      {(centre || trouvee) && (
        <Bulle vue={vue} trou={trou} occupe={occupe} erreur={geste.isError} onFaire={faire} />
      )}
    </div>
  );
}

function agrandi(r: Rectangle): Rectangle {
  const j = JOUR_AUTOUR;
  return { x: r.x - j, y: r.y - j, largeur: r.largeur + j * 2, hauteur: r.hauteur + j * 2 };
}

/** Quatre bandes autour du trou : elles arrêtent les clics, le trou les laisse passer. */
function Autour({ trou }: { trou: Rectangle }) {
  const droite = trou.x + trou.largeur;
  const bas = trou.y + trou.hauteur;
  return (
    <>
      <div className="visite-bande" style={{ left: 0, top: 0, right: 0, height: Math.max(0, trou.y) }} />
      <div className="visite-bande" style={{ left: 0, top: bas, right: 0, bottom: 0 }} />
      <div className="visite-bande" style={{ left: 0, top: trou.y, width: Math.max(0, trou.x), height: trou.hauteur }} />
      <div className="visite-bande" style={{ left: droite, top: trou.y, right: 0, height: trou.hauteur }} />
    </>
  );
}

function Bulle({
  vue,
  trou,
  occupe,
  erreur,
  onFaire,
}: {
  vue: VueDeVisite;
  trou: Rectangle | null;
  occupe: boolean;
  erreur: boolean;
  onFaire: (quoi: 'passer' | 'quitter') => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [place, setPlace] = useState<Place | null>(null);
  const centre = vue.sorte === 'centre';

  // La bulle se mesure, puis se pose du côté où elle tient.
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const taille = { largeur: el.offsetWidth, hauteur: el.offsetHeight };
    const fenetre = { largeur: window.innerWidth, hauteur: window.innerHeight };
    if (!trou) {
      setPlace({
        left: (fenetre.largeur - taille.largeur) / 2,
        top: Math.max(16, (fenetre.hauteur - taille.hauteur) / 2),
        cote: null,
        fleche: 0,
      });
      return;
    }
    setPlace(placeDeLaBulle(trou, taille, vue.cote, fenetre));
  }, [trou, vue.etape, vue.cote]);

  const fleche = place?.cote ? styleDeLaFleche(place) : null;

  return (
    <div
      ref={ref}
      className={`visite-bulle${centre ? ' visite-bulle--centre' : ''}`}
      style={place ? { left: place.left, top: place.top } : { visibility: 'hidden' }}
      aria-live="polite"
    >
      {fleche && <span className="visite-fleche" style={fleche} aria-hidden="true" />}

      {!centre && (
        <>
          <div className="visite-haut">
            <span className="visite-ecran">{vue.nom}</span>
            <span>
              {vue.numero} sur {vue.total}
            </span>
          </div>
          <div className="visite-progres" aria-hidden="true">
            <i style={{ width: `${(vue.numero / vue.total) * 100}%` }} />
          </div>
        </>
      )}

      <h3>{vue.titre}</h3>
      {vue.phare && <p className="visite-phare">{vue.phare}</p>}
      {vue.paragraphes.map((morceaux, i) => (
        <p key={i}>
          <TexteDuTuto morceaux={morceaux} />
        </p>
      ))}

      <div className="visite-actions">
        {erreur && <em className="visite-erreur">Ça n’a pas marché, réessaie</em>}
        {vue.sorte === 'clic' ? (
          <>
            <span className="visite-consigne">À toi</span>
            <button type="button" className="visite-lien" onClick={() => onFaire('quitter')} disabled={occupe}>
              Passer la visite
            </button>
          </>
        ) : (
          <>
            <button type="button" className="visite-lien" onClick={() => onFaire('quitter')} disabled={occupe}>
              {vue.autre}
            </button>
            <button type="button" className="bouton" onClick={() => onFaire('passer')} disabled={occupe} autoFocus>
              {vue.bouton}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

/** La flèche : un carré tourné, sur le bord de la bulle qui regarde la zone. */
function styleDeLaFleche(place: Place): React.CSSProperties {
  switch (place.cote) {
    case 'bas':
      return { top: -8, left: place.fleche };
    case 'haut':
      return { bottom: -8, left: place.fleche };
    case 'droite':
      return { left: -8, top: place.fleche };
    case 'gauche':
      return { right: -8, top: place.fleche };
    default:
      return {};
  }
}
